import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Heading, HStack, IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Table, Tbody, Td, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon, RepeatIcon } from "@chakra-ui/icons";
import rbacService from "../services/rbacService";
import type { Menu, Application } from "../../rbac/types";

const MenusPage: React.FC = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Menu[]>([]);
  const [apps, setApps] = useState<Application[]>([]);
  const [search, setSearch] = useState("");

  const [editing, setEditing] = useState<Menu | null>(null);
  const [form, setForm] = useState<{ app_id: string | ""; menu_key: string; display_name: string; path: string; }>({ app_id: "", menu_key: "", display_name: "", path: "" });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(m => m.menu_key.toLowerCase().includes(q) || m.display_name?.toLowerCase().includes(q) || m.path.toLowerCase().includes(q));
  }, [items, search]);

  const load = async () => {
    setLoading(true);
    try {
      const [menus, applications] = await Promise.all([
        rbacService.getMenus({ skip: 0, limit: 1000 }),
        rbacService.getApplications({ skip: 0, limit: 1000 }),
      ]);
      setItems(menus);
      setApps(applications);
    } catch (e: any) {
      toast({ status: "error", title: "Gagal memuat data", description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ app_id: "", menu_key: "", display_name: "", path: "" });
    onOpen();
  };

  const openEdit = (row: Menu) => {
    setEditing(row);
    setForm({ app_id: row.app_id, menu_key: row.menu_key, display_name: row.display_name || "", path: row.path });
    onOpen();
  };

  const submit = async () => {
    try {
      if (!form.app_id || !form.menu_key || !form.path) {
        toast({ status: "warning", title: "Lengkapi app, menu_key, dan path" });
        return;
      }
      if (editing) {
        await rbacService.updateMenu(editing.id, { ...form, app_id: form.app_id });
        toast({ status: "success", title: "Menu diperbarui" });
      } else {
        await rbacService.createMenu({ ...form, app_id: form.app_id });
        toast({ status: "success", title: "Menu dibuat" });
      }
      onClose();
      await load();
    } catch (e: any) {
      toast({ status: "error", title: "Operasi gagal", description: e?.response?.data?.detail || e?.message });
    }
  };

  const remove = async (row: Menu) => {
    if (!window.confirm(`Hapus menu '${row.menu_key}'?`)) return;
    try {
      await rbacService.deleteMenu(row.id);
      toast({ status: "success", title: "Menu dihapus" });
      await load();
    } catch (e: any) {
      toast({ status: "error", title: "Gagal menghapus", description: e?.response?.data?.detail || e?.message });
    }
  };

  return (
    <Box p={4}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">Menus</Heading>
        <HStack>
          <IconButton aria-label="refresh" icon={<RepeatIcon />} onClick={() => load()} isLoading={loading} />
          <Button leftIcon={<AddIcon />} colorScheme="green" onClick={openCreate}>New</Button>
        </HStack>
      </Flex>

      <HStack mb={3}>
        <Input placeholder="Cari menu..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </HStack>

      <Table size="sm" variant="striped">
        <Thead>
          <Tr>
            <Th>ID</Th>
            <Th>App</Th>
            <Th>Key</Th>
            <Th>Nama</Th>
            <Th>Path</Th>
            <Th isNumeric>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {filtered.map((row) => (
            <Tr key={row.id}>
              <Td>{row.id}</Td>
              <Td>{apps.find(a => a.id === row.app_id)?.app_name || row.app_id}</Td>
              <Td>{row.menu_key}</Td>
              <Td>{row.display_name}</Td>
              <Td>{row.path}</Td>
              <Td isNumeric>
                <HStack justify="flex-end">
                  <IconButton aria-label="edit" icon={<EditIcon />} onClick={() => openEdit(row)} size="sm" />
                  <IconButton aria-label="delete" icon={<DeleteIcon />} onClick={() => remove(row)} colorScheme="red" size="sm" />
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? "Edit Menu" : "Create Menu"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Select placeholder="Pilih aplikasi" value={form.app_id} onChange={(e) => setForm({ ...form, app_id: e.target.value ? e.target.value : "" })} mb={3}>
              {apps.map(a => (
                <option key={a.id} value={a.id}>{a.app_name}</option>
              ))}
            </Select>
            <Input placeholder="menu_key (unik per app)" value={form.menu_key} onChange={(e) => setForm({ ...form, menu_key: e.target.value })} mb={3} />
            <Input placeholder="display_name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} mb={3} />
            <Input placeholder="path (contoh: /admin/roles)" value={form.path} onChange={(e) => setForm({ ...form, path: e.target.value })} />
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="green" onClick={submit} isDisabled={!form.app_id || !form.menu_key.trim() || !form.path.trim()}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default MenusPage;
